import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Redirect, Route } from "react-router-dom";
import { getSessionPortal, isAuth } from "../utils/Auth";
import { ROUTE_PATHS } from "../utils/RoutesPaths";
import { AUTH_CONTEXT_TYPE, isClientCompanySelectionPath } from "../utils/authPortal";
import {
  clientRequiresCompanySelection,
  getClientPortalLogin,
  getClientMustChangePasswordLockRedirect,
  isAllowedPathWhileClientMustChangePassword,
  isClientMustChangePasswordLocked,
  resolveClientPostLoginRoute,
} from "../utils/clientPortal";
import {
  getCompanyMustChangePasswordLockRedirect,
  isAllowedPathWhileCompanyMustChangePassword,
  isCompanyMustChangePasswordLocked,
  mustCompleteCompanyRegistration,
} from "../utils/companyRegistration";

function createPublicRouteRenderer(Component) {
  function PublicRouteRenderer(props) {
    const { location } = props;
    if (
      isClientCompanySelectionPath(location.pathname) &&
      !isAuth()
    ) {
      return <Redirect to={{ pathname: ROUTE_PATHS.clientLogin }} />;
    }
    if (!isAuth()) {
      return <Component {...props} />;
    }
    if (
      getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT &&
      isClientCompanySelectionPath(location.pathname)
    ) {
      const clientLogin = getClientPortalLogin();
      if (isClientMustChangePasswordLocked(clientLogin)) {
        return (
          <Redirect
            push={false}
            to={getClientMustChangePasswordLockRedirect(clientLogin)}
          />
        );
      }
      if (!clientRequiresCompanySelection(clientLogin)) {
        return (
          <Redirect
            to={{ pathname: resolveClientPostLoginRoute(clientLogin) }}
          />
        );
      }
      return <Component {...props} />;
    }
    // Before `mustCompleteCompanyRegistration` so routes like pending-approval cannot bypass
    // an outstanding `must_change_password`.
    if (isClientMustChangePasswordLocked()) {
      if (!isAllowedPathWhileClientMustChangePassword(location.pathname)) {
        return (
          <Redirect
            push={false}
            to={getClientMustChangePasswordLockRedirect()}
          />
        );
      }
      return <Component {...props} />;
    }
    if (isCompanyMustChangePasswordLocked()) {
      if (!isAllowedPathWhileCompanyMustChangePassword(location.pathname)) {
        return (
          <Redirect
            push={false}
            to={getCompanyMustChangePasswordLockRedirect()}
          />
        );
      }
      return <Component {...props} />;
    }
    // Incomplete company signup: still has a token — allow public pages (login, etc.)
    // without forcing /events. Step enforcement runs only under /company/signup/* (RegistrationFlow).
    if (mustCompleteCompanyRegistration()) {
      return <Component {...props} />;
    }
    const sessionPortal = getSessionPortal();
    let home = ROUTE_PATHS.events;
    if (sessionPortal === AUTH_CONTEXT_TYPE.COMPANY) {
      home = ROUTE_PATHS.companyHome;
    } else if (sessionPortal === AUTH_CONTEXT_TYPE.CLIENT) {
      const clientLogin = getClientPortalLogin();
      home = clientRequiresCompanySelection(clientLogin)
        ? ROUTE_PATHS.clientSelectCompany
        : resolveClientPostLoginRoute(clientLogin);
    }
    return <Redirect to={{ pathname: home }} />;
  }

  const name = Component.displayName || Component.name || "Component";
  PublicRouteRenderer.displayName = `PublicRoute(${name})`;

  PublicRouteRenderer.propTypes = {
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
    match: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    staticContext: PropTypes.object,
  };

  return PublicRouteRenderer;
}

const PublicRoute = ({ component: Component, ...rest }) => {
  const RouteComponent = useMemo(
    () => createPublicRouteRenderer(Component),
    [Component]
  );

  return <Route {...rest} component={RouteComponent} />;
};

PublicRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
};

export default PublicRoute;
